import RoundButton from '../../components/round-button/RoundButton';
import styles from './shareInProject.module.less';
import { useState } from 'react';
import Spinner from '../../components/spinner/Spinner';
import { useDispatch, useSelector } from 'react-redux';
import { showErrorModal } from '../../global-message/global-message-actions';
import RoundedButtonPrimary from '../../components/button/rounded-button-primary/RoundedButtonPrimary';
import Modal from '../../components/modal/Modal';
import { BodyText, SectionTitle } from '../../components/typography/Heading';
import Image from '../../components/image/Image';
import { canUseNativeShare } from '../project-selectors';
import { getUserAgent } from '../../utils/browserDetector';

const ShareInProjectButton = ({
    label = 'Del…',
    faIcon = 'fas fa-share',
    urlToShare,
    titleToShare,
    textToShare,
    urlToImageToShare,
    trackEventName = 'share',
    trackEventFunction,
    onClickFallback,
    buttonClassName,
    containerClassName,
    buttonText,
    buttonType = RoundButton,
    horizontal,
}) => {
    const dispatch = useDispatch();
    const [sharing, setSharing] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [persistedSaveData, setPersistedShareData] = useState(null);

    const useNativeShare = useSelector((state) => canUseNativeShare(getUserAgent(state)));
    const useFallback = !useNativeShare || (!urlToShare && !titleToShare && !textToShare && !urlToImageToShare);

    const filesArrayFromImage = async () => {
        const imageResponse = await fetch(urlToImageToShare);
        const blob = await imageResponse.blob();
        const contentType = imageResponse.headers?.get('content-type');
        const fileType = contentType.split('/')[1];
        return [
            new File([blob], `bilde.${fileType}`, {
                type: contentType,
                lastModified: new Date().getTime(),
            }),
        ];
    };

    const webShare = async () => {
        const shareData = {
            url: urlToShare,
            title: titleToShare,
            text: textToShare,
            files: urlToImageToShare ? await filesArrayFromImage() : [],
        };

        if (navigator.canShare(shareData)) {
            try {
                await navigator.share(shareData);
            } catch (err) {
                // This is the error we get if we don't have permission to share
                // i.e. the transient activation has expired
                if (err.toString().includes('NotAllowedError')) {
                    setPersistedShareData(shareData);
                    setShowConfirmModal(true);
                } else if (err.toString().includes('AbortError')) {
                    // This is the error we get if the user cancels the share
                    // i.e. the user clicks the back button
                    // We do nothing here
                } else {
                    throw err;
                }
            }
        } else if (onClickFallback) {
            await onClickFallback();
        } else {
            throw Error('canShare = false og ingen fallback');
        }
    };

    const confirmShare = async () => {
        try {
            await navigator.share(persistedSaveData);
        } catch (err) {
            if (!err.toString().includes('AbortError')) {
                dispatch(showErrorModal('Klarte ikke å dele.'));
            }
        } finally {
            closeModal();
        }
    };

    const closeModal = () => {
        setPersistedShareData(null);
        setShowConfirmModal(false);
    };

    const Button = buttonType;

    return (
        <div className={[styles.shareComponent, containerClassName, horizontal ? styles.horizontal : ''].join(' ')}>
            <Button
                onClick={async () => {
                    trackEventFunction && trackEventFunction();
                    setSharing(true);
                    if (useFallback) {
                        onClickFallback && (await onClickFallback());
                    } else {
                        await webShare().catch((err) => {
                            if (!err.toString().includes('AbortError')) {
                                dispatch(showErrorModal('Klarte ikke å dele.'));
                            }
                        });
                    }
                    setSharing(false);
                }}
                className={buttonClassName}
                category={trackEventName}
                alt={label}
                label={urlToShare}
                disabled={sharing}
            >
                {sharing && <Spinner className={styles.buttonSpinner} />}
                {!sharing && (
                    <i className={faIcon} title={label}>
                        <span className="sr-only">{label}</span>
                    </i>
                )}
                {buttonText && <span>{buttonText}</span>}
            </Button>
            <Modal
                contentLabel="Viser meldinger"
                className={styles.modal}
                overlayClassName={styles.modalOverlay}
                isOpen={showConfirmModal}
                onRequestClose={closeModal}
            >
                <div className={styles.modalContent}>
                    <SectionTitle>Fullfør deling</SectionTitle>
                    <BodyText>Bildet er klart!</BodyText>
                    {persistedSaveData?.files && (
                        <Image className={styles.modalImage} src={URL.createObjectURL(persistedSaveData.files[0])} />
                    )}
                    <BodyText> Trykk her å dele det 🥳</BodyText>
                    <RoundedButtonPrimary className={styles.shareConfirmButton} onClick={confirmShare}>
                        Del
                    </RoundedButtonPrimary>
                </div>
            </Modal>
            {label && <div className={styles.shareDescription}>{label}</div>}
        </div>
    );
};

export default ShareInProjectButton;
